import { Component, effect, inject } from '@angular/core';
import { Meta } from '@angular/platform-browser';
import { RouterModule } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import Keycloak from 'keycloak-js';
import { CookieComponent, CookieStore } from '@mwe/ui/components';
import { EnvironmentService, PopupService, ProfileService } from '@mwe/services';
import { KeycloakRefreshService } from '@shared-ikp/service/keycloak-refresh.service';
import { KeycloakEventLoggerService } from '@shared-ikp/service/keycloakEventLogger.service';
import { IkpEnvironmentService } from './service/environment/ikp-environment.service';
import { MazeComponent } from './components/maze/maze.component';
import { version } from './environments/version';

@Component({
  selector: 'ikp-root',
  standalone: true,
  imports: [RouterModule, MazeComponent],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  providers: [ProfileService, EnvironmentService],
})
export class AppComponent {
  private readonly keycloak = inject(Keycloak);
  private readonly translate = inject(TranslateService);
  private readonly envService = inject(IkpEnvironmentService);
  private readonly metaService = inject(Meta);
  private readonly cookieStore = inject(CookieStore);
  private readonly popupService = inject(PopupService);

  protected readonly isMazeAnalyticsEnabled = this.envService.getMazePropertyId() !== '';

  constructor() {
    inject(KeycloakRefreshService);
    if (!this.envService.isProduction()) {
      inject(KeycloakEventLoggerService);
    }

    this.initI18n();
    this.setVersionMeta();

    effect(() => this.displayCookieConsentIfRequired());
  }

  private initI18n(): void {
    this.translate.setFallbackLang('de');
    this.translate.addLangs(['de', 'en']);
    this.translate.use('de');
  }

  private setVersionMeta(): void {
    this.metaService.addTag({ name: 'version', content: version }, true);
  }

  private displayCookieConsentIfRequired(): void {
    const { acceptedVersion, version, data } = this.cookieStore;

    const isImpersonating = this.keycloak.tokenParsed?.impersonator;
    const isCookieVersionAccepted = acceptedVersion() && version() && acceptedVersion() === version();

    if (!isImpersonating && data() && !isCookieVersionAccepted) {
      this.popupService.openV2({
        id: 'cookieConsent',
        component: CookieComponent,
        dismissable: false,
        titleKey: 'cookie-banner.title',
        modalSize: 'xl',
      });
    }
  }
}
